import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth.store';
import toast from 'react-hot-toast';
import SplashScreen from '@/components/shared/SplashScreen';

const schema = z.object({
  email: z.string().email('Email invalide'),
  password: z.string().min(1, 'Mot de passe requis'),
});

type FormData = z.infer<typeof schema>;

export default function LoginPage() {
  const navigate = useNavigate();
  const { setAuth, isAuthenticated, user } = useAuthStore();
  const [splashDone, setSplashDone] = useState(false);

  if (isAuthenticated && user) {
    if (user.role === 'CLIENT') return <Navigate to="/app/client/dashboard" replace />;
    if (user.role === 'JURISTE') return <Navigate to="/app/juriste/dashboard" replace />;
    return <Navigate to="/app/admin/dashboard" replace />;
  }

  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const handleSplashDone = () => setSplashDone(true);

  const onSubmit = async (data: FormData) => {
    try {
      const res = await api.post('/auth/login', data);
      const { user, accessToken, refreshToken } = res.data.data;
      setAuth(user, accessToken, refreshToken);

      if (user.must_change_password) {
        navigate('/change-password', { replace: true });
        return;
      }

      if (user.role === 'CLIENT') navigate('/app/client/dashboard');
      else if (user.role === 'JURISTE') navigate('/app/juriste/dashboard');
      else navigate('/app/admin/dashboard');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Identifiants incorrects';
      toast.error(msg);
    }
  };

  return (
    <>
      {!splashDone && <SplashScreen onDone={handleSplashDone} />}

      {/* Full-screen photo background */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, backgroundImage: 'url(/bg_login.jpg)', backgroundSize: 'cover', backgroundPosition: 'center' }} />
      {/* Warm dark vignette on the right where the card sits */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 1, background: 'linear-gradient(to left, rgba(15,13,8,0.72) 0%, rgba(15,13,8,0.2) 55%, transparent 100%)' }} />

      {/* Page shell — card anchored to the right */}
      <div className="login-shell" style={{
        minHeight: '100dvh',
        position: 'relative',
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: 'clamp(16px, 4vw, 40px)',
        opacity: splashDone ? 1 : 0,
        transition: 'opacity 0.6s ease',
      } as React.CSSProperties}>

        {/* Compact login card */}
        <div style={{
          width: '100%',
          maxWidth: 340,
          background: 'rgba(255,255,255,0.93)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRadius: 16,
          border: '1px solid rgba(255,255,255,0.85)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
          padding: '28px 26px 20px',
          boxSizing: 'border-box',
        } as React.CSSProperties}>

          {/* Logo + brand */}
          <div style={{ marginBottom: 22, textAlign: 'center' }}>
            <div style={{ height: 100, backgroundImage: 'url(/logo_vaeg_assist.png)', backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', marginBottom: 6 }} />
            <div style={{ fontSize: 10, color: '#9B9282', letterSpacing: '.08em', textTransform: 'uppercase' }}>Votre cabinet juridique externalisé</div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Email</label>
              <input
                {...register('email')}
                type="email"
                autoComplete="email"
                placeholder="vous@entreprise.fr"
                style={{
                  width: '100%', padding: '9px 12px', boxSizing: 'border-box',
                  background: '#fff', border: '1.5px solid #DDD0A8', borderRadius: 8,
                  color: '#111827', fontSize: 13, outline: 'none',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  WebkitAppearance: 'none',
                } as React.CSSProperties}
                onFocus={e => { e.target.style.borderColor = '#B8960A'; e.target.style.boxShadow = '0 0 0 3px rgba(184,150,10,0.15)'; }}
                onBlur={e => { e.target.style.borderColor = '#DDD0A8'; e.target.style.boxShadow = 'none'; }}
              />
              {errors.email && <p style={{ color: '#ef4444', fontSize: 10, marginTop: 3 }}>{errors.email.message}</p>}
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 5 }}>Mot de passe</label>
              <input
                {...register('password')}
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                style={{
                  width: '100%', padding: '9px 12px', boxSizing: 'border-box',
                  background: '#fff', border: '1.5px solid #DDD0A8', borderRadius: 8,
                  color: '#111827', fontSize: 13, outline: 'none',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  WebkitAppearance: 'none',
                } as React.CSSProperties}
                onFocus={e => { e.target.style.borderColor = '#B8960A'; e.target.style.boxShadow = '0 0 0 3px rgba(184,150,10,0.15)'; }}
                onBlur={e => { e.target.style.borderColor = '#DDD0A8'; e.target.style.boxShadow = 'none'; }}
              />
              {errors.password && <p style={{ color: '#ef4444', fontSize: 10, marginTop: 3 }}>{errors.password.message}</p>}
            </div>

            <div style={{ textAlign: 'right', marginTop: -4 }}>
              <Link to="/forgot-password" style={{ fontSize: 11, color: '#B8960A', textDecoration: 'none', fontWeight: 500 }}>
                Mot de passe oublié ?
              </Link>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                width: '100%', padding: '10px',
                background: isSubmitting ? '#D4B866' : 'linear-gradient(135deg, #D4B866 0%, #8A7008 100%)',
                color: '#fff', border: 'none', borderRadius: 8,
                fontSize: 13, fontWeight: 700, cursor: isSubmitting ? 'not-allowed' : 'pointer',
                letterSpacing: '0.03em',
                boxShadow: '0 4px 16px rgba(138,112,8,0.4)',
                WebkitAppearance: 'none',
                touchAction: 'manipulation',
              } as React.CSSProperties}
            >
              {isSubmitting ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>

          <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #FAF2DE', textAlign: 'center' }}>
            <p style={{ fontSize: 9, color: '#9ca3af', margin: 0, letterSpacing: '0.08em' }}>
              © 2026 VÆG CONFORMITÉ — Tous droits réservés
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
